import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { AngularEditorConfig } from '@kolkov/angular-editor';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ApiService, AttachmentEntityType, AttachmentType } from '@pos-app/core-ui';
import { KEYS, ORDER_ATTACHMENT_TYPE } from '@pos-app/data';
import { ToastrService } from 'ngx-toastr';
import { Subject, BehaviorSubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-order-card-notes',
    templateUrl: './order-card-notes.component.html',
    styleUrls: ['./order-card-notes.component.scss'],
    standalone: false
})
export class OrderCardNotesComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('content') modalBox: ElementRef;

  @Input() currentEventData: any;
  @Input() orderNumber: string;
  @Input() orderType: string;
  @Input() orderCompany: string;
  @Input() preloadAttachments: any;
  @Input() isReadOnly: boolean;
  @Output() reloadedAttachments = new EventEmitter<void>();
  @Output() closeActiveNoteModal = new EventEmitter<boolean>();
  @Output() openAddEditNoteView = new EventEmitter<boolean>();

  newNoteForm: UntypedFormGroup;
  attachedFile: File;
  allAttachments: any;
  editorConfig: AngularEditorConfig;
  SIZE_LIMIT = 15 * 1024 * 1024; // 15MB
  noteInEdit = null;
  unSubscribe$ = new Subject<void>();
  ORDER_ATTACHMENT_TYPE = ORDER_ATTACHMENT_TYPE;
  displayNoteGrid: boolean = true;
  userEmail = JSON.parse(localStorage.getItem(KEYS.jdeSession)).UserID;
  noteModelRef: any;
  private _loadingSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  loading$ = this._loadingSubject.asObservable();
  get loading(): boolean {
    return this._loadingSubject.value;
  }
  set loading(v: boolean) {
    this._loadingSubject.next(v);
  }
  entityId: string;
  entityType: AttachmentEntityType;
  orderDetails: any;
  madeChanges: any;

  constructor(
    private fb: UntypedFormBuilder,
    private toastr: ToastrService,
    private apiService: ApiService,
    private activeModal: NgbActiveModal,
    private modalService: NgbModal
  ) {
    this.newNoteForm = this.fb.group({
      note: [''],
      link: [''],
      noteType: [''],
    });

    this.editorConfig = {
      editable: true,
      spellcheck: false,
      height: '15rem',
      minHeight: '5rem',
      placeholder: 'Enter note here...',
      translate: 'no',
      defaultParagraphSeparator: 'p',
      toolbarHiddenButtons: [
        ['fontName', 'heading'],
        [
          'fontSize',
          'textColor',
          'backgroundColor',
          'customClasses',
          'link',
          'unlink',
          'insertImage',
          'insertVideo',
          'insertHorizontalRule',
          'toggleEditorMode',
        ],
      ],
    };
  }

  ngOnInit() {
    this.newNoteForm.valueChanges.pipe(takeUntil(this.unSubscribe$)).subscribe((formValue) => {
      if (formValue.noteType === '') {
        this.displayNoteGrid = true;
      } else {
        this.displayNoteGrid = false;
      }
    });
  }

  ngOnDestroy() {
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.preloadAttachments && changes.preloadAttachments.currentValue) {
      this.allAttachments = this.preloadAttachments;
    }
  }

  private reloadAttachments() {
    this.reloadedAttachments.emit();
  }

  selectNoteType() {
    this.newNoteForm.patchValue({
      note: '',
      link: '',
    });
  }

  saveAttachment() {
    if (this.noteInEdit) {
      this.updateItem({
        id: this.noteInEdit._id,
        data: this.newNoteForm.value.noteType === ORDER_ATTACHMENT_TYPE.note ? this.newNoteForm.value.note : this.newNoteForm.value.link,
        display: this.noteInEdit.display,
      });
      this.resetNoteForm();
      return;
    }
    if (this.attachedFile) {
      if (this.attachedFile.size > this.SIZE_LIMIT) {
        this.toastr.error('File is too big. Max allowed is 10MB');
      } else {
        this.apiService
          .uploadAttachment({
            entityType: 'order',
            entityId: `${this.currentEventData?.OrderNumber.toString()}|${this.orderType}|${this.orderCompany}`,
            type: ORDER_ATTACHMENT_TYPE.file as AttachmentType,
            file: this.attachedFile,
          })
          .subscribe(
            () => {
              this.attachedFile = null;
              this.resetNoteForm();
              this.reloadAttachments();
              this.toastr.success('File uploaded');
            },
            (e) => {
              this.toastr.error('Upload failed');
            }
          );
      }
    } else {
      this.apiService
        .uploadAttachment({
          entityType: 'order',
          entityId: `${this.currentEventData?.OrderNumber.toString()}|${this.orderType}|${this.orderCompany}`,
          type: this.newNoteForm.value.noteType as AttachmentType,
          noteOrLink: this.newNoteForm.value.note || this.newNoteForm.value.link,
        })
        .subscribe(
          (x) => {
            this.toastr.success(`${this.newNoteForm.value.noteType === 'note' ? 'Note' : 'Link'} added`);
            this.resetNoteForm();
            this.reloadAttachments();
          },
          (e) => {
            this.toastr.error('Action failed');
          }
        );
    }
  }

  onFileChange(event) {
    if (event.target.files && event.target.files.length > 0) {
      this.attachedFile = event.target.files.item(0);
    }
  }

  downloadFile(item) {
    this.apiService.downloadFile(item._id, item.name).subscribe((x) => {});
  }

  deleteItem(item) {
    this.resetNoteForm();
    this.apiService.deleteAttachment(item._id, 'D', this.userEmail).subscribe((x) => {
      this.reloadAttachments();
      this.toastr.success('Order note deleted');
    });
  }

  updateItem(data) {
    this.apiService.updateAttachment(data).subscribe((x) => {
      this.reloadAttachments();
      this.toastr.success('Order note updated');
    });
  }

  editNote(item) {
    this.noteInEdit = item;
    this.newNoteForm.patchValue({
      ...{ [item.type]: item.data },
      noteType: item.type,
    });
  }

  resetNoteForm() {
    this.noteInEdit = null;
    this.newNoteForm.patchValue({
      note: '',
      noteType: '',
      link: '',
    });
  }

  formatTime(time) {
    // JDE time doesn't put a 0 at the start when less than 10am
    if (parseInt(time.slice(0, 2)) > 24) {
      return ['0' + time.slice(0, 1), ':', time.slice(1, 3)].join('');
    } else {
      return [time.slice(0, 2), ':', time.slice(2, 4)].join('');
    }
  }

  closeModala() {
    this.closeActiveNoteModal.emit(true);
  }

  openAddEdit() {
    this.openAddEditNoteView.emit();
  }
}

